import { z } from "zod";
import type { components } from "#/schema";
import { Beweegthema, GroepeerType } from "#/schema.nswag";
import { apiEnum } from "#/modules/util.helpers/zod-enum";

const lockedLeerlingSchema = z.object({
  leerlingId: z.string(),
  groep: z.number(),
}) satisfies z.ZodType<components["schemas"]["LesgroepLeerlingLockModel"]>;

/** Fallback aantal groepen when the URL nor the docent's voorkeur provides one. */
export const FALLBACK_AANTAL_GROEPEN = 4;

/** Typed URL search for the start-lesson wizard (all steps share the parent route's schema). */
export const startLessonFlowSearchSchema = z.object({
  // Filter data used to create the groups
  klasIds: z.array(z.string()).catch([]).default([]),
  // Deliberately no schema default: "absent" means "use the docent's voorkeur". Step 3's
  // beforeLoad backfills it into the URL (like `seed`) so shared step-3 links reproduce the
  // same groups for users with a different voorkeur.
  aantalGroepen: z.number().int().optional().catch(undefined),
  beweegthema: apiEnum(Beweegthema).nullable().catch(null).default(null),
  groepeerType: apiEnum(GroepeerType).catch("Heterogeen").default("Heterogeen"),
  seed: z.string().nullable().catch(null).default(null),
  lockedLeerlingen: z.array(lockedLeerlingSchema).catch([]).default([]),
  // Leerlingen left out of the verdeling: "doet niet mee" at a shuffle, or absent at a redistribution.
  excludedLeerlingen: z.array(z.string()).catch([]).default([]),
  beweegactiviteiten: z.array(z.string()).catch([]).default([]),
  sociaalEmotioneelThemas: z.array(z.string()).catch([]).default([]),

  // When set, the wizard runs in EDIT mode for this les id (update instead of create).
  // Portal keyed edit mode off an `/edit/les-flow/...` pathname; the TanStack port drives it
  // off this search param so the same `/start/les-flow/*` routes serve both create and edit.
  editLesId: z.string().nullable().catch(null).default(null),

  // Filter data for functionality, not used to create the groups
  beweegactiviteitenreeksSearchTerm: z.string().nullable().catch(null).default(null),
  sociaalEmotioneelThemasReeksSearchTerm: z.string().nullable().catch(null).default(null),
  doelenTab: z.string().catch("beweegactiviteitenreeksen").default("beweegactiviteitenreeksen"),
  scheduledDate: z.string().nullable().catch(null).default(null),
  /** When false: only school's own goals (excludes Beweegbaas defaults). */
  includeDefaultsBeweeg: z.boolean().catch(true).default(true),
  includeDefaultsSociaal: z.boolean().catch(true).default(true),
});

export type StartLessonFilters = z.infer<typeof startLessonFlowSearchSchema>;

/**
 * Canonical default values, derived from the schema itself so they can never drift from it.
 * Paired with the route's `stripSearchParams` middleware to keep default-valued params out of
 * the URL (nuqs parity with the RR7 portal).
 */
export const startLessonFlowSearchDefaults: StartLessonFilters = startLessonFlowSearchSchema.parse(
  {},
);

/**
 * Pre-seed the wizard search from an existing les (edit mode). Portal read the les id from the
 * route params; here it is threaded through `editLesId` so the create/edit flows share one route.
 */
export function getStartLessonFiltersFromLesEditData(
  lesEditData: components["schemas"]["GetLesEditDataResponse"],
  lesId: string,
): StartLessonFilters {
  return {
    ...startLessonFlowSearchDefaults,
    editLesId: lesId,
    scheduledDate: lesEditData.date,
    klasIds: lesEditData.klasIds,
    aantalGroepen: lesEditData.filter.aantalGroepen,
    groepeerType: lesEditData.filter.groepeerType,
    beweegactiviteiten: lesEditData.beweegactiviteiten,
    sociaalEmotioneelThemas: lesEditData.sociaalEmotioneelThemas,
    seed: lesEditData.filter.seed,
    lockedLeerlingen: lesEditData.filter.lockedLeerlingen || [],
    excludedLeerlingen: lesEditData.filter.excludedLeerlingen || [],
  };
}
